import { useState, type ReactNode } from 'react'
import { Calendar, ChevronRight, History, MapPin, Music, Users, type LucideIcon } from 'lucide-react'
import invariant from 'tiny-invariant'
import type { SearchArtist, SearchEvent } from '@shared/searchIndex'
import type { Group } from '@/types'
import type { PersonSearchResult } from '@shared/peopleSearch'
import type { Appearance } from '@/lib/searchRanking'
import { formatDayShort, formatEventDateRange } from '@/lib/formatDate'
import { cn } from '@/lib/utils'
import { useTimeFormat } from '@/hooks/useTimeFormat'
import { Badge } from '../ui/badge'
import { Skeleton } from '../ui/skeleton'
import { UserAvatar } from '../UserAvatar'
import { searchStrings } from './strings'

// Past events stay searchable ("which festival was that?"); the label marks
// them as over without dimming the row like a disabled one.
// Past events stay searchable ("which festival was that?"); the label marks them
// as over without dimming the row like a disabled one. Far marks results outside
// the viewer's reach, so a far event can't pass for a local one at a glance. Past
// wins: distance is moot once it's over.
function StatusBadge({ past, away }: { past?: boolean; away?: boolean }) {
  if (!past && !away) return null
  return (
    <Badge variant="outline" className="shrink-0 text-muted-foreground">
      {past ? <History /> : <MapPin />}
      {past ? searchStrings().past : searchStrings().far}
    </Badge>
  )
}

// The result's own image, or its type icon on a muted tile when it has none
// (or the image fails to load). Artists are round, events and groups square.
export function SearchThumb({ src, icon: Icon, round }: { src?: string; icon: LucideIcon; round?: boolean }) {
  const [failed, setFailed] = useState(false)
  const shape = cn('size-10 shrink-0 overflow-hidden', round ? 'rounded-full' : 'rounded-md')
  if (!src || failed) {
    return (
      <div className={cn(shape, 'flex items-center justify-center bg-muted')}>
        <Icon className="size-4 text-muted-foreground" />
      </div>
    )
  }
  return (
    <img src={src} alt="" loading="lazy" decoding="async" onError={() => setFailed(true)} className={cn(shape, 'object-cover')} />
  )
}

const joinMeta = (parts: (string | undefined)[]) => parts.filter(Boolean).join(' · ')
const joinWhen = (day: string, time: string) => (time ? `${day} ${time}` : day)

export function ResultButton({ onClick, className, children }: { onClick: () => void; className?: string; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full cursor-pointer items-center gap-3 rounded-lg px-2 py-2 text-left text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none',
        className,
      )}
    >
      {children}
    </button>
  )
}

function RowSkeleton() {
  return (
    <div className="flex items-center gap-3 px-2 py-2">
      <Skeleton className="size-10 shrink-0 rounded-md" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className="h-3.5 w-2/5" />
        <Skeleton className="h-3 w-3/5" />
      </div>
    </div>
  )
}

export function EventResultRow({
  loading, event, past, away, locale, onSelect,
}: { loading?: boolean; event?: SearchEvent; past?: boolean; away?: boolean; locale?: string; onSelect?: () => void }) {
  if (loading) return <RowSkeleton />
  invariant(event, 'EventResultRow: event is required when not loading')
  invariant(locale, 'EventResultRow: locale is required when not loading')
  invariant(onSelect, 'EventResultRow: onSelect is required when not loading')

  return (
    <ResultButton onClick={onSelect}>
      <SearchThumb src={event.imageUrl} icon={Calendar} />
      <div className="min-w-0 flex-1">
        <div className={cn('truncate font-medium', event.cancelled && 'line-through')}>{event.name}</div>
        <div className="truncate text-xs text-muted-foreground">
          {joinMeta([event.venueName, event.city, formatEventDateRange(event.startDate, event.endDate, locale)])}
        </div>
      </div>
      <StatusBadge past={past} away={away} />
    </ResultButton>
  )
}

export function ArtistResultGroup({
  loading, artist, appearances, locale, onSelect, onSelectArtist,
}: {
  loading?: boolean
  artist?: SearchArtist
  appearances?: Appearance[]
  locale?: string
  onSelect?: (appearance: Appearance) => void
  /** Opens the artist page from the header row. */
  onSelectArtist?: () => void
}) {
  const { formatClock } = useTimeFormat()
  if (loading) return <RowSkeleton />
  invariant(artist, 'ArtistResultGroup: artist is required when not loading')
  invariant(appearances, 'ArtistResultGroup: appearances is required when not loading')
  invariant(locale, 'ArtistResultGroup: locale is required when not loading')
  invariant(onSelect, 'ArtistResultGroup: onSelect is required when not loading')
  const s = searchStrings()

  return (
    <div className="py-1">
      {onSelectArtist ? (
        <ResultButton onClick={onSelectArtist} className="pt-1 pb-0 font-medium">
          <SearchThumb src={artist.imageUrl} icon={Music} round />
          <span className="min-w-0 flex-1 truncate">{artist.name}</span>
          <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        </ResultButton>
      ) : (
        <div className="flex items-center gap-3 px-2 pt-1 text-sm font-medium">
          <SearchThumb src={artist.imageUrl} icon={Music} round />
          <span className="truncate">{artist.name}</span>
        </div>
      )}
      {appearances.map(a => {
        const key = a.kind === 'set' ? `set:${a.set.id}` : `lineup:${a.event.id}`
        // Appearance rows carry no location otherwise; a far one names its city.
        const awayCity = a.away && !a.past ? a.event.city : undefined
        const meta = a.kind === 'set'
          ? joinMeta([a.event.name, joinWhen(formatDayShort(a.set.day, locale), a.set.startTime && formatClock(a.set.startTime)), a.set.stageName, awayCity])
          : joinMeta([a.event.name, formatEventDateRange(a.event.startDate, a.event.endDate, locale), s.lineup, awayCity])
        return (
          <ResultButton
            key={key}
            onClick={() => onSelect(a)}
            className="py-1.5 pl-[3.75rem] text-muted-foreground"
          >
            <span className={cn('min-w-0 flex-1 truncate', a.event.cancelled && 'line-through')}>{meta}</span>
            <StatusBadge past={a.past} away={a.away} />
          </ResultButton>
        )
      })}
    </div>
  )
}

export function GroupResultRow({ loading, group, onSelect }: { loading?: boolean; group?: Group; onSelect?: () => void }) {
  if (loading) return <RowSkeleton />
  invariant(group, 'GroupResultRow: group is required when not loading')
  invariant(onSelect, 'GroupResultRow: onSelect is required when not loading')

  return (
    <ResultButton onClick={onSelect}>
      <SearchThumb src={group.imageUrl} icon={Users} />
      <span className="truncate font-medium">{group.name}</span>
    </ResultButton>
  )
}

export function PersonResultRow({ loading, person, onSelect }: { loading?: boolean; person?: PersonSearchResult; onSelect?: () => void }) {
  if (loading) return <RowSkeleton />
  invariant(person, 'PersonResultRow: person is required when not loading')
  invariant(onSelect, 'PersonResultRow: onSelect is required when not loading')

  return (
    <ResultButton onClick={onSelect}>
      <UserAvatar profile={person} fallbackName={person.displayName} size="lg" />
      <span className="truncate font-medium">{person.displayName}</span>
    </ResultButton>
  )
}
